import { create } from 'zustand'

/**
 * Width of a docked upsert panel — `Modal` with `placement="side"`. One number for the whole app:
 * `.sidePanel` in `shared/ui/overlay/Modal/Modal.module.css` draws it, and `AppLayout` reserves it.
 */
export const DOCKED_PANEL_WIDTH = 560

/**
 * Which docked panels are showing, and how wide they are.
 *
 * A docked `Modal` is `position: fixed`, so the page under it knows nothing about it — the
 * disposition board used to run on underneath and lose its last hours to the panel. The panel
 * reports itself here while it shows; `AppLayout` reserves that width on the right of the page so
 * the content shrinks *beside* the panel, and `NavBar` collapses to an icon rail to give the width
 * back. Lives in `shared` because the writer is a shared component; the `app` layer only reads.
 *
 * Keyed by panel rather than a single flag: both upsert modals on the board stay mounted and a
 * block click can swap one for the other inside a single commit. A flag would be cleared by the
 * closing panel's cleanup after the opening one had set it.
 */
interface DockedPanelState {
  panels: Record<string, number>
  open: (id: string, width: number) => void
  close: (id: string) => void
}

export const useDockedPanelStore = create<DockedPanelState>((set) => ({
  panels: {},
  open: (id, width) => set((s) => ({ panels: { ...s.panels, [id]: width } })),
  close: (id) =>
    set((s) => {
      if (!(id in s.panels)) return s
      const { [id]: _removed, ...rest } = s.panels
      return { panels: rest }
    }),
}))

/** The widest showing docked panel in px, or `null` while none shows. Primitive, so stable. */
export const useDockedPanelWidth = (): number | null =>
  useDockedPanelStore((s) => {
    const widths = Object.values(s.panels)
    return widths.length ? Math.max(...widths) : null
  })
